import React, { useState, useEffect } from 'react';
import './TaskModal.css';
import { X, Users } from 'lucide-react';
import UserAvatar from './UserAvatar';

function CreateTaskModal({ status, project, onClose, onCreate }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    columnId: status,
    status: 'todo',
    priority: 'medium',
    assignedTo: [],
    dueDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onCreate(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const toggleAssignee = (userId) => {
    setFormData({
      ...formData,
      assignedTo: formData.assignedTo.includes(userId)
        ? formData.assignedTo.filter(id => id !== userId)
        : [...formData.assignedTo, userId]
    });
  };

  const removeAssignee = (userId) => {
    setFormData({
      ...formData,
      assignedTo: formData.assignedTo.filter(id => id !== userId)
    });
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAssignedUser = (userId) => {
    return project.members?.find(m => m._id === userId);
  };

  const filteredMembers = project.members?.filter(m => 
    m.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  ) || [];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showAssigneeDropdown && !e.target.closest('.assignee-dropdown-wrapper')) {
        setShowAssigneeDropdown(false);
        setAssigneeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssigneeDropdown]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
              Create New Task
            </h2>
            <button onClick={onClose} className="btn btn-ghost" style={{ padding: '8px', width: '40px', height: '40px' }}>
              <X size={20} />
            </button>
          </div>

          {error && (
            <div className="error-message" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label>Task title</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Enter task title..."
                autoFocus
              />
            </div>

            <div className="input-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows="4"
                placeholder="Add description (Markdown supported)..."
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="input-group">
                <label>Priority</label>
                <select
                  value={formData.priority}
                  onChange={e => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="input-group">
                <label>Due date</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>
            </div>

            {/* Assignee Dropdown */}
            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <Users size={16} />
                Assign To
              </label>

              {/* Selected assignees */}
              {formData.assignedTo.length > 0 && (
                <div className="selected-assignees">
                  {formData.assignedTo.map(userId => {
                    const user = getAssignedUser(userId);
                    if (!user) return null;
                    return (
                      <div key={userId} className="assignee-tag">
                        <UserAvatar user={user} className="avatar" style={{ width: '20px', height: '20px', fontSize: '10px' }} />
                        <span>{user.name}</span>
                        <button type="button" onClick={() => removeAssignee(userId)} className="remove-assignee">
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Dropdown */}
              <div className="assignee-dropdown-wrapper">
                <button 
                  type="button"
                  className="assignee-dropdown-toggle"
                  onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                >
                  <Users size={14} />
                  Add assignee...
                </button>

                {showAssigneeDropdown && (
                  <div className="assignee-dropdown">
                    <input
                      type="text"
                      className="assignee-search"
                      placeholder="Search members..."
                      value={assigneeSearch}
                      onChange={e => setAssigneeSearch(e.target.value)}
                      autoFocus
                    />
                    <div className="assignee-list">
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map(member => {
                          const isAssigned = formData.assignedTo.includes(member._id);
                          return (
                            <div
                              key={member._id}
                              className={`assignee-item ${isAssigned ? 'assigned' : ''}`}
                              onClick={() => {
                                toggleAssignee(member._id);
                                if (!isAssigned) {
                                  setShowAssigneeDropdown(false);
                                  setAssigneeSearch('');
                                }
                              }}
                            >
                              <UserAvatar user={member} className="avatar" style={{ width: '28px', height: '28px', fontSize: '12px' }} />
                              <span>{member.name}</span>
                              {isAssigned && <span className="assigned-checkmark">✓</span>}
                            </div>
                          );
                        })
                      ) : (
                        <div className="assignee-empty">No members found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading} style={{ flex: 1 }}>
                {loading ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateTaskModal;
